/**
 * @module @/load-app-config
 * @summary 启动时通过 ENV.confAPI 拉取门户 App 配置
 * @author keep
 * @since 2026-05-19
 */

import { observer } from '@yimoka/react';
import { type PropsWithChildren, useEffect, useState } from 'react';

import {
  fetchAppConfig,
  type IAppConfig,
  shouldFetchAppConfig,
} from '@/app-config';

export type LoadAppConfigProps = PropsWithChildren<{
  /** 配置加载成功后的回调 */
  onLoad?: (config: IAppConfig) => void;
}>;

/**
 * 拉取并注入 App 配置；未配置远程地址时直接渲染子节点。
 *
 * @param props - 子节点与 onLoad
 * @returns React 元素
 */
export const LoadAppConfig = observer(({ onLoad, children }: LoadAppConfigProps) => {
  const [ready, setReady] = useState(!shouldFetchAppConfig());

  useEffect(() => {
    if (!shouldFetchAppConfig()) {
      return;
    }
    let cancelled = false;
    void fetchAppConfig()
      .then((config) => {
        if (!cancelled) {
          onLoad?.(config);
          setReady(true);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setReady(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [onLoad]);

  if (!ready) {
    return null;
  }

  return <>{children}</>;
});
